import { gql, useQuery } from '@apollo/client'
import * as React from 'react'
import { TypeToggle, TypeToggleInner } from '../../../../common/src/components/core'
import { handleError } from '../../../../common/src/data/errors'
import { addFeedback } from '../../../../common/src/feedback'
import keyboard, { KeyAction } from '../../../../common/src/keyboard'
import memoize from '../../../../common/src/memoize'
import { TaskUnitTypesQuery, UnitType, TaskUnitTypesQueryVariables } from '../../../../common/src/types.gen'
import { apolloWebClient } from '../../apolloClientForWeb'

const setThreadUnitTypesMutation = gql`
  mutation SetThreadUnitTypes($threadId: Int!, $unitTypes: [UnitType!]!) {
    setThreadUnitTypes(threadId: $threadId, unitTypes: $unitTypes) {
      id
      unitTypesInterested
    }
  }
`

export const taskUnitTypesQuery = gql`
  query TaskUnitTypes($taskId: Int!) {
    task(taskId: $taskId) {
      id
      unitTypes
      unitTypesShowableOnAnyDate
    }
  }
`

interface UnitTypesChooserProps {
  data?: TaskUnitTypesQuery
  unitTypes?: UnitType[]
  unitTypesShowableOnAnyDate?: UnitType[]
  taskId: number
  threadId?: number
  showingId?: number
  selectedUnitTypes: UnitType[]
  onSelect?: (selectedUnitTypes: UnitType[]) => void
  setStaleThread: (stale: boolean) => void
  style?: React.CSSProperties
}

class UnitTypesChooserImpl extends React.PureComponent<UnitTypesChooserProps & { data?: TaskUnitTypesQuery }> {
  private unitTypeHandlers: Record<string, () => void> = {}

  private toggleUnitType(unitType: UnitType) {
    const { selectedUnitTypes } = this.props
    const ix = selectedUnitTypes.indexOf(unitType)
    let newSelectedUnitTypes
    if (ix < 0) {
      newSelectedUnitTypes = selectedUnitTypes.concat([unitType])
    } else {
      newSelectedUnitTypes = selectedUnitTypes.slice()
      newSelectedUnitTypes.splice(ix, 1)
    }
    this.onEdit(newSelectedUnitTypes)
  }

  private onEdit(unitTypes: UnitType[]) {
    if (this.props.onSelect) {
      this.props.onSelect(unitTypes)
      return // short-circuit graphql mutation if handler provided
    }
    if (this.props.setStaleThread) {
      this.props.setStaleThread(true)
    }
    const { threadId, showingId } = this.props
    apolloWebClient
      .mutate({
        mutation: setThreadUnitTypesMutation,
        variables: { threadId, showingId, unitTypes },
      })
      .then(() => addFeedback('Set unit types'))
      .catch(handleError)
  }

  componentDidMount() {
    keyboard.addListener(this.onKeyboardBound)
  }

  componentWillUnmount() {
    keyboard.removeListener(this.onKeyboardBound)
  }

  private onKeyboardBound = (action: KeyAction) => {
    const unitTypes = this.getUnitTypes() || []
    if (action === KeyAction.toggleUnitTypeStudio && unitTypes.includes(UnitType.STUDIO)) {
      this.toggleUnitType(UnitType.STUDIO)
    } else if (action === KeyAction.toggleUnitTypeBR1 && unitTypes.includes(UnitType.BR1)) {
      this.toggleUnitType(UnitType.BR1)
    } else if (action === KeyAction.toggleUnitTypeBR2 && unitTypes.includes(UnitType.BR2)) {
      this.toggleUnitType(UnitType.BR2)
    } else if (action === KeyAction.toggleUnitTypeBR3 && unitTypes.includes(UnitType.BR3)) {
      this.toggleUnitType(UnitType.BR3)
    } else if (action === KeyAction.toggleUnitTypeBR4 && unitTypes.includes(UnitType.BR4)) {
      this.toggleUnitType(UnitType.BR4)
    } else if (action === KeyAction.toggleUnitTypeBR5 && unitTypes.includes(UnitType.BR5PLUS)) {
      this.toggleUnitType(UnitType.BR5PLUS)
    }
  }

  private getUnitTypes() {
    const { data } = this.props

    if (data == null) {
      return this.props.unitTypes
    } else if (data.task == null) {
      return []
    }
    return data.task.unitTypes
  }

  render() {
    const { data, selectedUnitTypes } = this.props

    let unitTypes
    let unitTypesShowableOnAnyDate: UnitType[] | undefined
    if (data == null) {
      unitTypes = this.props.unitTypes
      unitTypesShowableOnAnyDate = this.props.unitTypesShowableOnAnyDate
    } else if (data.task == null) {
      return null
    } else {
      unitTypes = data.task.unitTypes
      unitTypesShowableOnAnyDate = data.task.unitTypesShowableOnAnyDate
    }
    const allUT = unitTypes == null ? [] : unitTypes.slice()
    const selUT = selectedUnitTypes == null ? [] : selectedUnitTypes.slice()
    if (allUT.length === 0) {
      return null
    }
    return (
      <>
        {allUT.map(u => (
          <TypeToggle
            key={u}
            isSelected={selUT.includes(u)}
            isAvailable={unitTypesShowableOnAnyDate != null && unitTypesShowableOnAnyDate.includes(u)}
            onClick={memoize(
              this.unitTypeHandlers,
              u as string,
              (unitType: UnitType) => () => this.toggleUnitType(unitType)
            )}
          >
            {u}
            <TypeToggleInner
              isSelected={selUT.includes(u)}
              isAvailable={unitTypesShowableOnAnyDate != null && unitTypesShowableOnAnyDate.includes(u)}
            >
              {u}
            </TypeToggleInner>
          </TypeToggle>
        ))}
      </>
    )
  }
}

export default (props: UnitTypesChooserProps) => {
  if (props.unitTypes != null) {
    return <UnitTypesChooserImpl {...props} />
  }

  const { data } = useQuery<TaskUnitTypesQuery, TaskUnitTypesQueryVariables>(taskUnitTypesQuery, {
    variables: { taskId: props.taskId },
  })

  return <UnitTypesChooserImpl {...props} data={data} />
}
